/**
 * Cart API Service
 * Provides methods for interacting with the cart API
 */
export default ($axios, store) => ({
  /**
   * Get store_id from the Vuex store
   * @returns {string} Store ID
   */
  getStoreId() {
    return store.getters.store?.store_id || store.getters.store?.id || store.getters.store?._id;
  },

  /**
   * Get cart_id from the Vuex store
   * @returns {string|null} Cart ID
   */
  getCartId() {
    return store.getters.cart?.id || null;
  },

  /**
   * Add product to cart
   * @param {string} barcode - Product barcode
   * @param {number} quantity - Quantity to add (default: 1)
   * @param {Array} variation - Product variations (optional)
   * @returns {Promise<Object>} Cart response data
   */
  async addItem(barcode, quantity = 1, variation = []) {
    const { data } = await $axios.post('/cart/item', {
      store_id: this.getStoreId(),
      cart_id: this.getCartId(),
      barcode,
      quantity,
      variation
    });
    return data;
  },

  /**
   * Increase item quantity
   * @param {string} barcode - Product barcode
   * @param {number} amount - Amount to increase (default: 1)
   * @returns {Promise<Object>} Cart response data
   */
  async increaseQuantity(barcode, amount = 1) {
    const { data } = await $axios.post('/cart/item/increase', {
      store_id: this.getStoreId(),
      cart_id: this.getCartId(),
      barcode,
      amount
    });
    return data;
  },

  /**
   * Decrease item quantity
   * @param {string} barcode - Product barcode
   * @param {number} amount - Amount to decrease (default: 1)
   * @param {boolean} removeOnZero - Remove item when quantity reaches 0 (default: true)
   * @returns {Promise<Object>} Cart response data
   */
  async decreaseQuantity(barcode, amount = 1, removeOnZero = true) {
    const { data } = await $axios.post('/cart/item/decrease', {
      store_id: this.getStoreId(),
      cart_id: this.getCartId(),
      barcode,
      amount,
      remove_on_zero: removeOnZero
    });
    return data;
  },

  /**
   * Remove item from cart
   * @param {string} barcode - Product barcode
   * @returns {Promise<Object>} Cart response data
   */
  async removeItem(barcode) {
    const { data } = await $axios.delete('/cart/item', {
      params: {
        store_id: this.getStoreId(),
        cart_id: this.getCartId(),
        barcode
      }
    });
    return data;
  },

  /**
   * Update specific cart path
   * @param {string} path - Path to update (e.g., 'shipping.address', 'user.phone')
   * @param {any} value - Value to set
   * @returns {Promise<Object>} Cart response data
   */
  async updatePath(path, value) {
    const { data } = await $axios.post('/cart/update-path', {
      store_id: this.getStoreId(),
      cart_id: this.getCartId(),
      path,
      value
    });
    return data;
  },

  /**
   * Bulk update multiple cart paths
   * @param {Array<{path: string, value: any}>} updates - Array of updates
   * @returns {Promise<Object>} Cart response data
   */
  async updatePaths(updates) {
    const { data } = await $axios.post('/cart/update-paths', {
      store_id: this.getStoreId(),
      cart_id: this.getCartId(),
      updates
    });
    return data;
  }
});
